import React, { useEffect, useState, useContext } from 'react';
import { AuthContext } from '../auth/AuthContext';
import { useNavigate } from 'react-router-dom';
import '../global.css';

const validPositions = ["Middle Blocker", "Setter", "Outside Hitter", "Libero", "Opposite"];

const PlayerBannerComponent = ({ playerId }) => {
  const { token, role } = useContext(AuthContext);
  const navigate = useNavigate();

  const [player, setPlayer] = useState(null);
  const [formState, setFormState] = useState(null);
  const [originalState, setOriginalState] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`https://vballbackend.onrender.com/api/player/${playerId}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => res.json())
      .then(data => {
        setPlayer(data);
        const initialState = {
          name: data.name || '',
          position: data.position || '',
          shirtNumber: data.shirtNumber || '',
          height: data.height || '',
          spikeHeight: data.spikeHeight || '',
          blockHeight: data.blockHeight || ''
        };
        setFormState(initialState);
        setOriginalState(initialState);
        setLoading(false);
      })
      .catch(err => {
        console.error("Error fetching player banner:", err);
        setLoading(false);
      });
  }, [playerId, token]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormState(prev => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const { name, position, shirtNumber, height, spikeHeight, blockHeight } = formState;

    if (name.length > 100) return alert("Name must be 100 characters or fewer."), false;
    if (!validPositions.includes(position)) return alert("Please select a valid position."), false;

    const shirt = parseInt(shirtNumber);
    if (isNaN(shirt) || shirt < 1 || shirt >= 100)
      return alert("Shirt number must be between 1 and 99."), false;

    const heightVal = parseInt(height);
    if (isNaN(heightVal) || heightVal <= 100 || heightVal >= 250)
      return alert("Height must be between 101 and 249 cm."), false;

    const spike = parseInt(spikeHeight);
    if (isNaN(spike) || spike < 100)
      return alert("Spike height must be at least 100 cm."), false;

    const block = parseInt(blockHeight);
    if (isNaN(block) || block < 0)
      return alert("Block height must be 0 or more."), false;

    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    const confirmed = window.confirm("Are you sure you want to save these changes?");
    if (!confirmed) return;

    const res = await fetch(`https://vballbackend.onrender.com/api/player/${playerId}/update`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(formState)
    });

    if (res.ok) {
      setPlayer(prev => ({ ...prev, ...formState }));
      setOriginalState(formState);
      setIsEditing(false);
    } else {
      alert("Failed to save changes.");
    }
  };

  const handleDiscard = () => {
    if (window.confirm("Discard all unsaved changes?")) {
      setFormState(originalState);
      setIsEditing(false);
    }
  };

  const handleDelete = async () => {
    const confirmed = window.confirm("Are you sure you want to permanently delete this player?");
    if (!confirmed) return;

    const res = await fetch(`https://vballbackend.onrender.com/api/player/${playerId}/delete`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` }
    });

    if (res.ok) {
      alert("Player deleted.");
      navigate('/players');
    } else {
      const err = await res.json();
      alert(`Failed to delete player: ${err.msg}`);
    }
  };

  const hasChanged = () => JSON.stringify(formState) !== JSON.stringify(originalState);

  if (loading || !player || !formState) {
    return <div className="container mt-4">Loading player banner...</div>;
  }

  const photoUrl = player.photoURL;

  return (
    <div className="container mt-6" style={{ width: '100%' }}>
      <div className="card" style={{ position: 'relative' }}>
        {/* Header */}
        <div className="flex-row" style={{ alignItems: 'center', marginBottom: '1.5rem' }}>
          <img
            src={photoUrl || 'https://placehold.co/100x100?text=Player'}
            alt="Player"
            style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              objectFit: 'cover'
            }}
          />
          {isEditing ? (
            <input
              type="text"
              name="name"
              className="ml-4"
              value={formState.name}
              onChange={handleChange}
              style={{ fontSize: '1.75rem', fontWeight: 'bold', flexGrow: 1 }}
            />
          ) : (
            <div style={{ fontSize: '1.75rem', fontWeight: 'bold', marginLeft: '1rem' }}>
              {player.name}
            </div>
          )}
        </div>

        {/* Player Info */}
        <div className="text-small" style={{ lineHeight: '1.8' }}>
          <p><strong>Preferred Position:</strong> {isEditing ? (
            <select name="position" value={formState.position} onChange={handleChange}>
              <option value="">Select</option>
              {validPositions.map(pos => (
                <option key={pos} value={pos}>{pos}</option>
              ))}
            </select>
          ) : player.position || 'N/A'}</p>

          <p><strong>Shirt Number:</strong> {isEditing ? (
            <input type="number" name="shirtNumber" value={formState.shirtNumber} onChange={handleChange} />
          ) : player.shirtNumber || 'N/A'}</p>

          <p><strong>Height:</strong> {isEditing ? (
            <input type="number" name="height" value={formState.height} onChange={handleChange} />
          ) : player.height || 'N/A'}</p>

          <p><strong>Spike Height:</strong> {isEditing ? (
            <input type="number" name="spikeHeight" value={formState.spikeHeight} onChange={handleChange} />
          ) : player.spikeHeight || 'N/A'}</p>

          <p><strong>Block Height:</strong> {isEditing ? (
            <input type="number" name="blockHeight" value={formState.blockHeight} onChange={handleChange} />
          ) : player.blockHeight || 'N/A'}</p>
        </div>

        {/* Controls (Only for Coaches) */}
        {role === 'coach' && (
          <div style={{ position: 'absolute', top: '1.25rem', right: '1.25rem', display: 'flex', gap: '0.5rem' }}>
            {!isEditing ? (
              <>
                <button className="btn-primary" onClick={() => setIsEditing(true)}>Edit</button>
                <button className="btn-danger" onClick={handleDelete}>Delete</button>
              </>
            ) : (
              <>
                <button
                  className="btn-primary"
                  onClick={handleSave}
                  disabled={!hasChanged()}
                  style={{ opacity: hasChanged() ? 1 : 0.5 }}
                >
                  Save
                </button>
                <button className="btn-secondary" onClick={handleDiscard}>Discard</button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PlayerBannerComponent;
